import { useState, useCallback, useEffect, useRef } from "react";
import { channelsApi } from "@/api/channels";
import type { Channel } from "@/types/channel";

export function useChannels() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initialLoadDone = useRef(false);

  const fetchChannels = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const res = await channelsApi.list();
      setChannels(res.data || []);
      setError(null);
      initialLoadDone.current = true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const createChannel = useCallback(
    async (data: Partial<Channel>) => {
      await channelsApi.create(data);
      await fetchChannels();
    },
    [fetchChannels],
  );

  const updateChannel = useCallback(
    async (id: number, data: Partial<Channel>) => {
      await channelsApi.update(id, data);
      await fetchChannels();
    },
    [fetchChannels],
  );

  const deleteChannel = useCallback(
    async (id: number) => {
      await channelsApi.delete(id);
      await fetchChannels();
    },
    [fetchChannels],
  );

  return {
    channels,
    loading,
    error,
    refetch: fetchChannels,
    createChannel,
    updateChannel,
    deleteChannel,
  };
}
